import { getAxiosInstance } from '@project/utils/request';

export interface GeojsonSearchResponse<P = GeoJSON.GeoJsonProperties> {
  type: 'FeatureCollection';
  _count: number;
  _from_count: number;
  features: Array<
    Omit<GeoJSON.Feature<GeoJSON.Geometry, P>, 'id'> & { id: number }
  >;
}

export interface RoadProperties {
  road_name: string;
}

export interface DistrictRoadProperties {
  district_id: number;
  district_name: string;
  road_name: string;
}

export interface DistrictProperties {
  administration: string | null;
  area_distr: number | null;
  area_farmlands: string | null;
  city_centr: string | null;
  district_name: string | null;
  header_administration: string | null;
  place: string | null;
  population: number | null;
  rasstoaynie: string | null;
  site: string | null;
}

export interface KilometersProperties {
  direction: number;
  district_id: number;
  num_km: number;
  road_id: number;
}

export interface KilometersSearchParams {
  district?: string;
  road: string;
  direction: string;
  numKm?: string;
  order?: string;
  srid?: number;
}

export interface RoadsSearchParams {
  page?: string;
  pageSize?: string;
  search?: string;
  district?: number;
  order?: string;
}

export const DEFAULT_TABLE_NAME = 'kilometers';

const DEFAULT_SRID = 3857;
const DEFAULT_PAGE_SIZE = 0;
const DEFAULT_LIMIT = 150;

export class APIGeoJSONSearch {
  readonly url = '/api/geojson/';
  readonly tableName = DEFAULT_TABLE_NAME;

  async list(
    params: KilometersSearchParams,
  ): Promise<GeojsonSearchResponse<KilometersProperties>> {
    return await getAxiosInstance().get<
      GeojsonSearchResponse<KilometersProperties>
    >(`${this.url}${this.tableName}/`, {
      params: {
        _limit: DEFAULT_LIMIT,
        _page_size: DEFAULT_PAGE_SIZE,
        _srid: params.srid ?? DEFAULT_SRID,
        road_id__exact: params.road,
        direction__exact: params.direction,
        district_id__exact: params.district,
        num_km__exact: params.numKm,
        _order: params.order,
      },
    });
  }

  async roads(
    params: RoadsSearchParams,
    signal?: AbortSignal,
  ): Promise<GeojsonSearchResponse<RoadProperties>> {
    return await getAxiosInstance().get(this.url + 'ro_roads/', {
      params: {
        _offset: params.page ?? '0',
        _limit: params.pageSize ?? '15',
        _order: params.order ?? 'road_name',
        district_id: params.district ?? null,
        road_name__icontains: params.search ?? null,
      },
      signal,
    });
  }

  async district_roads(
    params: RoadsSearchParams,
    signal?: AbortSignal,
  ): Promise<GeojsonSearchResponse<DistrictRoadProperties>> {
    return await getAxiosInstance().get(this.url + 'ro_district_roads/', {
      params: {
        _offset: params.page ?? '0',
        _limit: params.pageSize ?? '15',
        _order: params.order ?? 'road_name',
        district_id: params.district,
        road_name__icontains: params.search ?? null,
      },
      signal,
    });
  }

  async districts(): Promise<GeojsonSearchResponse<DistrictProperties>> {
    return await getAxiosInstance().get(this.url + 'districts/', {
      params: {
        _order: 'district_name',
      },
    });
  }
}
